var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var PhoneNumber_1;
import React, { Component } from 'react';
import { getCountryCallingCode, isSupportedCountry, parsePhoneNumberFromString } from 'libphonenumber-js/max';
import memoizeOne from 'memoize-one';
import { SensitiveData } from '@philips-internal/oswald-ui-constants';
import { AwesomeComponent, ComponentType } from '@philips-internal/oswald-ui-core';
import { bool, eventListener, expression, oneOf, string } from '@philips-internal/oswald-ui-core/lib/component/prop-types/index.js';
import { ChangeEvent, FocusEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { LocaleService, ModelService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { validators } from '@philips-internal/oswald-ui-model';
import { classNames, defaultClasses, enumValues, isFunction, noop } from '@philips-internal/oswald-ui-utils';
import { hideSensitiveData } from '../../utils/hide-sensitive-data.js';
import { FieldContext } from '../field/context/field-context.js';
import { Input } from '../input/input.js';
import { CountrySelect } from './components/country-select.js';
import { PhoneNumberAPI } from './phone-number-api.js';
import './phone-number.css';
// @ts-ignore: library's package.json is wrongly configured
const parsePhone = memoizeOne((value = '', defaultCountry = '', safe = true) => {
    const phone = parsePhoneNumberFromString(value, defaultCountry);
    if (!safe || !phone) {
        return;
    }
    const country = phone.country || defaultCountry;
    const countryCallingCode = isSupportedCountry(country) ? getCountryCallingCode(country) : '';
    const nationalNumber = phone.nationalNumber || value.replace(`+${countryCallingCode}`, '');
    const formattedNational = (phone.formatNational && phone.formatNational()) || nationalNumber;
    const prefixedCallingCode = countryCallingCode ? `+${countryCallingCode}` : '';
    return {
        country,
        countryCallingCode,
        formattedNational,
        nationalNumber,
        number() {
            return nationalNumber
                ? `${prefixedCallingCode}${nationalNumber}`
                : undefined;
        },
    };
});
let PhoneNumber = PhoneNumber_1 = class PhoneNumber extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            country: LocaleService.getCurrentLocaleCountry(),
            focused: false,
            number: undefined,
        };
        this.updateValidations = (value) => {
            if (!this.context) {
                return;
            }
            const validationRunner = ModelService.validate(value);
            validationRunner.apply(validators.PHONE_NUMBER);
            const validations = validationRunner.run();
            this.context.updateChildValidations(validations);
        };
        this.onFocus = () => {
            const { onFocus = noop } = this.props;
            this.setState({ focused: true }, () => {
                isFunction(onFocus) && onFocus(new FocusEvent());
            });
        };
        this.onBlur = () => {
            const { onBlur = noop } = this.props;
            this.setState({ focused: false }, () => {
                isFunction(onBlur) && onBlur(new FocusEvent());
            });
        };
        this.onChange = () => {
            const { value: oldValue, onChange } = this.props;
            const { country, number } = this.state;
            const phone = parsePhone(number, country);
            const newValue = phone?.number() ?? '';
            this.updateValidations(newValue);
            if (isFunction(onChange)) {
                onChange(new ChangeEvent({
                    oldValue,
                    prettyValue: phone?.number(),
                    value: newValue,
                }));
            }
        };
        this.onCountryChange = ({ value }) => {
            if (!value.code) {
                return;
            }
            this.setState({ country: value.code }, this.onChange);
        };
        this.onNumberChange = (event) => {
            this.setState({ number: event.value }, this.onChange);
        };
    }
    componentDidMount() {
        const { value } = this.props;
        this.updateValidations(value ?? '');
    }
    componentDidUpdate(prevProps) {
        const { country, number } = this.state;
        const { value } = this.props;
        const { value: oldValue } = prevProps;
        if ((value && value !== oldValue) || (value && !number)) {
            const statePhone = parsePhone(number, country);
            const propsPhone = parsePhone(value, country);
            if (this.match(propsPhone, statePhone)) {
                return;
            }
            this.setState({
                country: propsPhone?.country,
                number: propsPhone?.nationalNumber,
            });
        }
        else if (oldValue && !value) {
            this.setState({
                number: undefined,
            });
        }
    }
    componentWillUnmount() {
        this.context?.resetChildValidations();
    }
    match(phoneA, phoneB) {
        return phoneA?.country === phoneB?.country
            && phoneA?.nationalNumber === phoneB?.nationalNumber;
    }
    getParsedPhone() {
        const { country, number } = this.state;
        return parsePhone(number, country);
    }
    getValue() {
        const { focused } = this.state;
        const { readOnly, sensitiveData } = this.props;
        const phone = this.getParsedPhone();
        let value = focused ? phone?.nationalNumber : phone?.formattedNational;
        if (readOnly && value) {
            value = hideSensitiveData(value, sensitiveData, { replacer: /[0-9]/g });
        }
        return value;
    }
    render() {
        const { readOnly = PhoneNumber_1.defaultProps.readOnly, enabled = PhoneNumber_1.defaultProps.enabled, onKeyPress, onKeyDown, onKeyUp, placeholder, sensitiveData, strikethrough, valid = PhoneNumber_1.defaultProps.valid, } = this.props;
        const { country } = this.state;
        const containerClasses = classNames({
            'c-phone-number': true,
            'c-phone-number--invalid': !valid,
            'u-text-strikethrough': Boolean(strikethrough),
            ...defaultClasses({ enabled, readOnly, valid }),
        });
        return (React.createElement("div", { className: containerClasses },
            React.createElement(CountrySelect, { enabled: enabled, onChange: this.onCountryChange, readOnly: readOnly, sensitiveData: sensitiveData, value: country }),
            React.createElement(Input, { className: "c-phone-number__input", enabled: enabled, onBlur: this.onBlur, onChange: this.onNumberChange, onFocus: this.onFocus, onKeyDown: onKeyDown, onKeyPress: onKeyPress, onKeyUp: onKeyUp, placeholder: placeholder, readOnly: readOnly, value: this.getValue() })));
    }
};
PhoneNumber.contextType = FieldContext;
PhoneNumber.propTypes = {
    enabled: bool,
    onBlur: eventListener,
    onChange: eventListener,
    onFocus: eventListener,
    onKeyDown: eventListener,
    onKeyPress: eventListener,
    onKeyUp: eventListener,
    placeholder: expression,
    readOnly: bool,
    sensitiveData: oneOf(enumValues(SensitiveData)),
    strikethrough: bool,
    valid: bool,
    value: string,
};
PhoneNumber.defaultProps = {
    enabled: true,
    readOnly: false,
    sensitiveData: SensitiveData.NONE,
    valid: true,
};
PhoneNumber = PhoneNumber_1 = __decorate([
    AwesomeComponent({ api: PhoneNumberAPI, type: ComponentType.DATA_ENTRY })
], PhoneNumber);
export { PhoneNumber, };
//# sourceMappingURL=phone-number.js.map